import { BaseService } from './BaseService';
import type { PagedResult } from '../types/tenant';

/**
 * Safety gate severity levels.
 */
export const SafetyGateSeverity = {
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
  Critical: 'Critical',
} as const;

export type SafetyGateSeverity = typeof SafetyGateSeverity[keyof typeof SafetyGateSeverity];

/**
 * Safety gate status.
 */
export const SafetyGateStatus = {
  Pending: 'Pending',
  Approved: 'Approved',
  Denied: 'Denied',
  Timeout: 'Timeout',
  Cancelled: 'Cancelled',
} as const;

export type SafetyGateStatus = typeof SafetyGateStatus[keyof typeof SafetyGateStatus];

/**
 * Safety gate DTO.
 */
export interface SafetyGateDto {
  id: string;
  tenantId: string;
  projectId?: string;
  name: string;
  description?: string;
  actionPattern: string;
  severity: SafetyGateSeverity;
  requiresApproval: boolean;
  requiredApprovers?: number;
  autoApprovalTimeoutMinutes?: number;
  approverRolesJson?: string;
  conditionJson?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Safety gate execution DTO.
 */
export interface SafetyGateExecutionDto {
  id: string;
  safetyGateId: string;
  safetyGateName: string;
  agentSessionId?: string;
  toolExecutionId?: string;
  actionName: string;
  status: SafetyGateStatus;
  reason?: string;
  evaluatedAt: string;
  approvedAt?: string;
  approvedBy?: string;
  approvalComments?: string;
  approverIds: string[];
}

/**
 * Safety gate filters.
 */
export interface SafetyGateFilters {
  pageNumber?: number;
  pageSize?: number;
  severity?: SafetyGateSeverity;
  isActive?: boolean;
  searchTerm?: string;
}

/**
 * Create safety gate request.
 */
export interface CreateSafetyGateRequest {
  name: string;
  description?: string;
  actionPattern: string;
  severity: SafetyGateSeverity;
  requiresApproval: boolean;
  requiredApprovers?: number;
  autoApprovalTimeoutMinutes?: number;
  approverRolesJson?: string;
  conditionJson?: string;
  isActive: boolean;
}

/**
 * Update safety gate request.
 */
export interface UpdateSafetyGateRequest {
  name: string;
  description?: string;
  actionPattern: string;
  severity: SafetyGateSeverity;
  requiresApproval: boolean;
  requiredApprovers?: number;
  autoApprovalTimeoutMinutes?: number;
  approverRolesJson?: string;
  conditionJson?: string;
  isActive: boolean;
}

/**
 * Approve safety gate request.
 */
export interface ApproveSafetyGateRequest {
  comments?: string;
}

/**
 * Deny safety gate request.
 */
export interface DenySafetyGateRequest {
  reason: string;
}

/**
 * Safety gate service for managing safety gates and approvals.
 */
export class SafetyGateService extends BaseService {
  /**
   * Gets a paginated list of safety gates with optional filtering.
   */
  async getSafetyGates(filters?: SafetyGateFilters): Promise<PagedResult<SafetyGateDto>> {
    const queryParams = new URLSearchParams();
    if (filters?.pageNumber) queryParams.append('pageNumber', filters.pageNumber.toString());
    if (filters?.pageSize) queryParams.append('pageSize', filters.pageSize.toString());
    if (filters?.severity) queryParams.append('severity', filters.severity);
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive.toString());
    if (filters?.searchTerm) queryParams.append('searchTerm', filters.searchTerm);

    const url = `/safety-gates${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<SafetyGateDto>>(url);
  }

  /**
   * Gets a safety gate by ID.
   */
  async getSafetyGate(id: string): Promise<SafetyGateDto> {
    return this.get<SafetyGateDto>(`/safety-gates/${id}`);
  }

  /**
   * Creates a safety gate.
   */
  async createSafetyGate(data: CreateSafetyGateRequest): Promise<SafetyGateDto> {
    return this.post<SafetyGateDto>('/safety-gates', data);
  }

  /**
   * Updates a safety gate.
   */
  async updateSafetyGate(id: string, data: UpdateSafetyGateRequest): Promise<SafetyGateDto> {
    return this.put<SafetyGateDto>(`/safety-gates/${id}`, data);
  }

  /**
   * Deletes a safety gate.
   */
  async deleteSafetyGate(id: string): Promise<void> {
    return this.delete(`/safety-gates/${id}`);
  }

  /**
   * Gets pending safety gate approvals.
   */
  async getPendingApprovals(safetyGateId?: string): Promise<SafetyGateExecutionDto[]> {
    const queryParams = new URLSearchParams();
    if (safetyGateId) queryParams.append('safetyGateId', safetyGateId);

    const url = `/safety-gates/approvals/pending${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<SafetyGateExecutionDto[]>(url);
  }

  /**
   * Approves a safety gate execution.
   */
  async approveSafetyGate(executionId: string, data: ApproveSafetyGateRequest): Promise<void> {
    return this.post(`/safety-gates/executions/${executionId}/approve`, data);
  }

  /**
   * Denies a safety gate execution.
   */
  async denySafetyGate(executionId: string, data: DenySafetyGateRequest): Promise<void> {
    return this.post(`/safety-gates/executions/${executionId}/deny`, data);
  }
}

export const safetyGateService = new SafetyGateService();
